import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'

export interface MerkeRow {
  merke: string | null
  count: number
  andel: number // share of all eierskifter in the period (0..1)
}

export interface MerkeVolumeResponse {
  rows: MerkeRow[]
  total: number
}

export interface ModellRow {
  merke: string | null
  modell: string | null
  count: number
  andel: number // share of all eierskifter in the period (0..1)
}

export interface ModellVolumeResponse {
  rows: ModellRow[]
  total: number
}

export interface ForstegangsaarRow {
  year: number
  count: number
}

export interface ForstegangsaarResponse {
  rows: ForstegangsaarRow[]
}

type BaseParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

/** Format a market-share fraction (0..1) as an nb-NO percent, e.g. "12,3 %". */
export function formatAndel(andel: number): string {
  return `${(andel * 100).toLocaleString('nb-NO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}

// Build merke-modell URLs with URLSearchParams so the literal `merke[]` key
// reaches FastAPI. axios' `paramsSerializer: { indexes: null }` strips the
// brackets off the key (sending `merke=Toyota`), which the backend — declaring
// `merke: List[str] = Query(alias="merke[]")` — never reads, silently returning
// ALL merker. The Priser page hit the same bug; see Priser/api.ts.
function buildMmQS(
  filters: BaseParams,
  extras: Record<string, string | number | string[] | undefined>,
): string {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  for (const k of filters.klasse) qs.append('klasse', k)
  // Omit varebil entirely for "alle" (undefined) — matches the backend's
  // 3-state convention; false = personbil, true = varebil.
  if (filters.varebil !== undefined) qs.set('varebil', String(filters.varebil))
  // Omit omregistrering when false/undefined — only send true when the filter is active.
  if (filters.omregistrering) qs.set('omregistrering', 'true')
  // Postnummer geo-filter
  if (filters.selgerPostnummer) for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  if (filters.kjoperPostnummer) for (const p of filters.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', p.trim())
  for (const [key, val] of Object.entries(extras)) {
    if (val === undefined) continue
    if (Array.isArray(val)) {
      for (const v of val) qs.append(key, v)
    } else {
      qs.set(key, String(val))
    }
  }
  return qs.toString()
}

export function fetchMerkeVolume(
  filters: BaseParams,
  limit = 50,
  offset = 0,
  q?: string,
): Promise<{ data: MerkeVolumeResponse }> {
  const qs = buildMmQS(filters, { limit, offset, q: q || undefined })
  return api.get(`/bruktbil/merke-modell/merke?${qs}`)
}

export function fetchModellVolume(
  filters: BaseParams,
  merkeFilter: string[],
  limit = 50,
  offset = 0,
  q?: string,
): Promise<{ data: ModellVolumeResponse }> {
  const qs = buildMmQS(filters, { 'merke[]': merkeFilter, limit, offset, q: q || undefined })
  return api.get(`/bruktbil/merke-modell/modell?${qs}`)
}

export function fetchForstegangsaar(
  filters: BaseParams,
): Promise<{ data: ForstegangsaarResponse }> {
  const qs = buildMmQS(filters, {})
  return api.get(`/bruktbil/merke-modell/forstegangsaar?${qs}`)
}

// The Sammenligning module compares merke × overdragelsesklasse. The chart
// buckets eierskifter by ISO week; the grid aggregates the whole sales period.
export interface PerUkeRow {
  week: string
  merke: string | null
  klasse: string | null
  count: number
}
export interface PerUkeResponse {
  rows: PerUkeRow[]
  total_series: number
}

export interface SammenligningRow {
  merke: string | null
  klasse: string | null
  eierskifter: number
  snittalder: number | null
}
export interface SammenligningResponse {
  rows: SammenligningRow[]
}

export interface MmSelection {
  merke: string[]
  aarMin?: number
  aarMax?: number
}

function selectionExtras(s: MmSelection): Record<string, string | number | string[] | undefined> {
  return {
    'merke[]': s.merke.length ? s.merke : undefined,
    aar_min: s.aarMin,
    aar_max: s.aarMax,
  }
}

// Typeahead option source — q is the substring the user typed.
export function fetchMerkeOptions(
  filters: BaseParams, q: string, aarMin?: number, aarMax?: number,
): Promise<{ data: MerkeVolumeResponse }> {
  const qs = buildMmQS(filters, { q, aar_min: aarMin, aar_max: aarMax, limit: 50 })
  return api.get(`/bruktbil/merke-modell/merke?${qs}`)
}

export function fetchPerUke(
  filters: BaseParams, sel: MmSelection,
): Promise<{ data: PerUkeResponse }> {
  const qs = buildMmQS(filters, selectionExtras(sel))
  return api.get(`/bruktbil/merke-modell/per-uke?${qs}`)
}

export function fetchSammenligning(
  filters: BaseParams, sel: MmSelection,
): Promise<{ data: SammenligningResponse }> {
  const qs = buildMmQS(filters, selectionExtras(sel))
  return api.get(`/bruktbil/merke-modell/sammenligning?${qs}`)
}
